.iconButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Base has a 1px transparent border so switching to variant-outline
   * (which sets border-color) doesn't shift layout by 1px. Same trick
   * Button uses. */
  border: 1px solid transparent;
  border-radius: var(--radius-4);
  background: transparent;
  color: var(--icon-default);
  cursor: pointer;
  box-sizing: border-box;
  transition:
    background-color var(--motion-duration-fast) ease,
    border-color var(--motion-duration-fast) ease,
    box-shadow var(--motion-duration-fast) ease,
    color var(--motion-duration-fast) ease;
}

.iconButton:focus-visible {
  outline: none;
  /* Match Button's Figma-derived 1px outset ring. --text-primary swaps
   * light/dark automatically — neutral-600 in light, near-white in dark —
   * which is what the Figma "Focus" state calls for. Sibling interactive
   * primitives share a consistent keyboard affordance. */
  box-shadow: 0 0 0 1px var(--text-primary);
}

.iconButton:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* ─── Sizes ──────────────────────────────────────────────────────── */
/* Sizes are padding-based rather than fixed width/height, matching the
 * Figma spec which uses `p-[4px]` (s, m) and `p-[8px]` (l). The box
 * sizes to glyph + padding + 2px transparent border, which leaves the
 * rendered size ~2px larger than the Figma target — same 2px offset
 * Button accepts for the same structural reason.
 *
 * `s` and `m` share the same 4px padding intentionally; the visual
 * delta between them is the glyph size (16 vs 20), driven by
 * GLYPH_SIZE in index.tsx — NOT by any CSS difference. Collapsed into
 * one rule so a future maintainer doesn't mistake two identical
 * padding blocks for a copy-paste bug and silently delete or diverge
 * one of them. */
.size-s,
.size-m {
  padding: var(--spacing-4);
}

.size-l {
  padding: var(--spacing-8);
}

/* ─── Variants ───────────────────────────────────────────────────── */

/* Default — transparent fill, no visible border. Hover paints
 * --icon-bg-hover (the semantic "icon button hover" surface). Used for
 * card dismiss buttons and other low-weight in-context actions.
 *
 * The default variant has no non-hover declarations: background, border,
 * and color are already set by the base .iconButton block. The class
 * identifier still needs to appear in this file (via the :hover selector
 * below) so CSS Modules exports it — otherwise `styles['variant-default']`
 * would be undefined, .filter(Boolean) in index.tsx would strip it, and
 * no class would ever attach to the button, making the hover rule
 * unreachable.
 * Future maintainers: do not add `background: transparent` here — it
 * duplicates the base and reads as load-bearing when it isn't. */
.variant-default:hover:not(:disabled) {
  background: var(--icon-bg-hover);
}

/* Outline — transparent fill with a visible 1px --icon-outline border.
 * Hover paints the same --icon-bg-hover surface while keeping the
 * border (border-color stays set from the base rule; no override
 * needed on hover because `border-color` is in the transition list
 * and nothing clears it between states). Used when the icon button
 * needs more weight (standalone on a surface without any surrounding
 * chrome). */
.variant-outline {
  border-color: var(--icon-outline);
}
.variant-outline:hover:not(:disabled) {
  background: var(--icon-bg-hover);
}
